'use client';
import { useEffect } from 'react';
import { isExpired } from '@/lib/subscription/subscription-timing';
import { createMiddlewareBrowserClient } from '@/lib/supabase/client';
import { getTenantIdFromSession } from '@/lib/tenant/session';
import { useSubscriptionStore } from '@/store/subscriptionStore';
import { useSubscriptionClock } from './useSubscriptionClock';

/**
 * Subscribes to Postgres changes on `tenant_subscriptions` for the current tenant.
 * On any INSERT / UPDATE / DELETE, re-fetches the active subscription.
 * Mount this once at the app root — it is a singleton by design.
 */
export function useSubscriptionListener() {
    const nowMs = useSubscriptionClock(60_000);
    const subscription = useSubscriptionStore((s) => s.subscription);
    const markExpired = useSubscriptionStore((s) => s.markExpired);
    // On every 60s tick: if active subscription just expired, write inactive to DB
    useEffect(() => {
    if (
      subscription?.status === 'active' &&
      subscription.id &&
      isExpired(subscription, nowMs)
    ) {
      markExpired(subscription.id);
    }
  }, [nowMs, subscription, markExpired]);
  useEffect(() => {
    const tenantId = getTenantIdFromSession();
    if (!tenantId) return;

    const { fetchActiveSubscription, fetchSubscriptionHistory } = useSubscriptionStore.getState();
    const supabase = createMiddlewareBrowserClient() as any;

    const channel = supabase
      .channel(`subscription-listener-${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tenant_subscriptions',
          filter: `tenant_id=eq.${tenantId}`,
        },
        async () => {
          await fetchActiveSubscription(tenantId);
          await fetchSubscriptionHistory(tenantId);
        },
      )
      .subscribe((status: string) => {
        // Fetch immediately on connect
        if (status === 'SUBSCRIBED') {
          fetchActiveSubscription(tenantId);
          fetchSubscriptionHistory(tenantId);
        }
        // Fetch on reconnect / error recovery
        if (['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED'].includes(status)) {
          fetchActiveSubscription(tenantId);
          fetchSubscriptionHistory(tenantId);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []); // Empty dep array — intentional singleton behaviour
}
